import { PageSection, PageSectionVariants, TextContent, Text, Alert, FormGroup, TextInput, TextArea, Form } from '@patternfly/react-core';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import './about-page.css';

import { StoreState } from '../../store/store';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { appVersionSelectors, labelingFetched, workspaceLabelingFetched } from '../../store/appVersion/appVersionSlice';
import { appVersionToString } from '../../store/appVersion/appVersionTypes';
import TabbedSupportedModelTypes, { ModelTypeContent } from '../../components/TabbedSupportedModelTypes/TabbedSupportedModelTypes';
import ModuleView from './ModuleView';
import { DISPLAY_VERSION } from '../../environments';
import { selectIntersectionOfSupportedModelTypes } from '../../store/configurationTarget/configurationTargetSelectors';
import LabelingInfo, { getLabelForIsForClinicalUse } from './LabelingInfo';

interface AboutPageProps {
    isEmbedded?: boolean;
}

const AboutPage = (props: AboutPageProps) => {
    const { isEmbedded } = props;

    // reload labeling whenever the page is reloaded
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(labelingFetched());
        dispatch(workspaceLabelingFetched());
    }, [dispatch]);

    const productLabels = useSelector((state: StoreState) => state.appVersion.productLabels);
    const productLabelError = useSelector((state: StoreState) => state.appVersion.productLabelError);
    const backendVersionAsString = useSelector((state: StoreState) => state.appVersion.backendVersionAsString);
    const appVersion = useSelector(appVersionSelectors.selectAppVersion);
    const workspaceLabeling = useSelector((state: StoreState) => state.appVersion.workspaceLabeling);
    const workspaceLabelingError = useSelector((state: StoreState) => state.appVersion.workspaceLabelError);
    const appConfig = useSelector(appConfigSelectors.selectAppConfig);
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const intersectionOfSupportedModelTypes = useSelector(selectIntersectionOfSupportedModelTypes);



    // print version information to console when about page is opened
    useEffect(() => {
        if (productLabels) {
            console.log(productLabels);
        }
        if (appVersion) {
            console.log(appVersionToString(appVersion));
        }
    }, [appVersion, productLabels]);

    const getWorkspaceInfoForClipboard = useCallback(() => {
        if (!workspaceLabeling || !deploymentInfo) {
            return '';
        }

        const { mvisionInfo } = deploymentInfo;

        const info = `
Software Version: ${workspaceLabeling.systemVersion}
Clinical Use: ${getLabelForIsForClinicalUse(workspaceLabeling.isClinical)}
Instructions for Use: ${mvisionInfo?.ifuUrl || ''}
Description: ${workspaceLabeling.description}
Address: ${mvisionInfo?.address?.company || ''}, ${mvisionInfo?.address?.street || ''}, ${mvisionInfo?.address?.location || ''}
UDI: ${workspaceLabeling.udi}
Manufacture Date: ${workspaceLabeling.manufactureDate}
Email: ${mvisionInfo?.email || ''}
Website: ${mvisionInfo?.webAddress || ''}`;

        return info.trim()
    }, [deploymentInfo, workspaceLabeling]);

    const modelTypeContent: ModelTypeContent[] = productLabels?.map(l => ({
        modelType: l.modelType,
        content: <ModuleView
            productLabel={l}
            mvisionInfo={deploymentInfo?.mvisionInfo}
        />
    })) || [];

    return (
        <>
            {!isEmbedded && (
                <PageSection variant={PageSectionVariants.default}>
                    <TextContent>
                        <Text component="h1">{t('page.about.title')}</Text>
                    </TextContent>
                </PageSection>
            )}

            {appConfig && appConfig.showErrorsInUi && productLabelError !== null && (
                <PageSection className="selectable">
                    <Alert variant="danger" isInline title="An error occurred when trying to retrieve labeling information">
                        <p>An error occurred when trying to retrieve labeling information:</p>
                        <p>{productLabelError}</p>
                    </Alert>
                </PageSection>
            )}

            {appConfig && appConfig.showErrorsInUi && workspaceLabelingError !== null && (
                <PageSection className="selectable">
                    <Alert variant="danger" isInline title={t("common.workspaceLabeling.fetchError")}>
                        <p>{t("common.workspaceLabeling.fetchError")}</p>
                        <p>{workspaceLabelingError}</p>
                    </Alert>
                </PageSection>
            )}

            <PageSection variant={PageSectionVariants.light} className="limit-width">
                <div className="labeling">
                    {workspaceLabeling && deploymentInfo && (
                        <div className="labeling-section" data-testid="about-workspace-labeling">
                            <LabelingInfo
                                mvisionInfo={deploymentInfo.mvisionInfo}
                                softwareVersion={workspaceLabeling.systemVersion}
                                clinicalUse={workspaceLabeling.isClinical}
                                description={workspaceLabeling.description}
                                udi={workspaceLabeling.udi}
                                manufacturer={workspaceLabeling.manufacturer}
                                manufactureDate={workspaceLabeling.manufactureDate}
                                getLabelInfoForClipboard={getWorkspaceInfoForClipboard}
                            />
                        </div>
                    )}
                    {intersectionOfSupportedModelTypes.length > 0 && (
                        <div className="modelTypeSection labeling-section" data-testid="about-module-labeling">
                            <TabbedSupportedModelTypes
                                content={modelTypeContent}
                            />
                        </div>
                    )}

                    <Form isHorizontal className="labeling-section">
                        {appVersion && (
                            <FormGroup
                                label={t('appTitle')}
                                fieldId="app-version-readonly" >
                                <TextInput value={`${DISPLAY_VERSION} (${appVersion.commit})`} type="text" id="app-version-readonly" readOnlyVariant="default" data-testid="about-mvision-configuration-version" />
                            </FormGroup>
                        )}

                        {backendVersionAsString && (
                            <FormGroup
                                label={t('page.about.backendAndWorkersAppName')}
                                fieldId="backend-version-readonly" >
                                <TextArea
                                    readOnly
                                    id="backend-version-readonly"
                                    value={backendVersionAsString}
                                    resizeOrientation='vertical'
                                    className="text-editor-area label"
                                    spellCheck="false"
                                    rows={7}
                                />
                            </FormGroup>
                        )}
                    </Form>

                </div>
            </PageSection>
        </>
    );
}

export default AboutPage;
